/**
 * @block TestUserSwitcher
 * @description Dev-only persona switcher for the dashboard header.
 *
 *   Reads `/seed-personas.json` (written by `npm run seed:personas` to
 *   `apps/dashboard/public/`). Renders a styled dropdown listing every
 *   test persona grouped by track. Picking one calls /auth/login with
 *   that persona's stored credentials, swaps the access token in
 *   storage, and reloads the page so every component re-fetches with
 *   the new identity.
 *
 *   Hidden in production builds (`import.meta.env.DEV` gate). Hidden
 *   silently when /seed-personas.json is absent — so non-dev installs
 *   never see the toggle.
 *
 *   This is a custom dropdown rather than a native <select> so it can
 *   match the design system (level badges, two-line rows, brand-tint
 *   group headers). Click-outside closes the menu; Escape too.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { storage } from '../../utils/storage';
import api from '../../api/client';
import { useRole } from '../dashboard/useRole.js';

import { Button } from '../elements/Button.jsx';
const TRACK_LABELS = {
  customer:       'Customer',
  reviewer:       'Reviewer',
  designer:       'Designer',
  developer:      'Developer',
  content_writer: 'Content writer',
  domain_expert:  'Domain expert',
  'owner:admin':  'Admin',
  'owner:super_admin': 'Super admin',
  'owner:manager':'Manager',
  'owner:finance':'Finance',
  'owner:support':'Support'
};
const trackLabel = (key) => TRACK_LABELS[key] || key;

// Tone per track group — keeps the dropdown readable without rainbow-
// ing everything. Owner-group rows get the brand tint, customer is
// neutral, worker tracks get a muted indigo.
const trackTone = (key) => {
  if (String(key).startsWith('owner:')) return 'brand';
  if (key === 'customer') return 'gray';
  return 'indigo';
};

const TONE = {
  brand:  { chip: 'bg-[var(--moksy-brand-tint)] text-[var(--moksy-brand-dark)] ring-[var(--moksy-brand-border)]' },
  indigo: { chip: 'bg-[#EEF2FF] text-[#3730A3] ring-[#C7D2FE]' },
  gray:   { chip: 'bg-[#F3F4F6] text-[#374151] ring-[var(--moksy-border)]' }
};

export default function TestUserSwitcher() {
  // Production hides it entirely. Vite sets DEV=true in `vite dev` mode
  // and DEV=false on `vite build`. Safe to early-return before hooks
  // here because DEV is a build-time constant — a given bundle is
  // either always dev or always prod, never both.
  if (!import.meta.env?.DEV) return null;

  // Operator-only — we use this to simulate other personas while
  // debugging. Plain customers and worker-track users (designer,
  // reviewer, etc) have no business toggling identities, so the
  // dropdown is hidden for them even in dev. `isOperator` is true
  // exactly for the owner-group roles (super_admin, admin, manager,
  // finance, support, content_moderator, …) — see useRole.js for the
  // full list.
  const { isOperator } = useRole() || {};

  const [personas, setPersonas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    fetch('/seed-personas.json')
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!mounted) return;
        const items = Array.isArray(data?.personas) ? data.personas : [];
        setPersonas(items);
      })
      .catch(() => { if (mounted) setPersonas([]); });
    return () => { mounted = false; };
  }, []);

  // Close on click-outside / Escape so the dropdown behaves like every
  // other menu in the app.
  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClickAway);
    document.addEventListener('keydown', onKey);
    setTimeout(() => inputRef.current?.focus(), 10);
    return () => {
      document.removeEventListener('mousedown', onClickAway);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Group + filter. Hook MUST be declared before any conditional
  // early-return below, otherwise hook-call count differs between
  // renders (empty → loaded) and React panics with a hooks-order
  // violation. The work inside is cheap when `personas` is empty, so
  // we just always compute and bail at the render site.
  const grouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const haystack = (p) =>
      `${p.email} ${p.levelName} ${p.trackKey} L${p.level}`.toLowerCase();
    const matched = q ? personas.filter((p) => haystack(p).includes(q)) : personas;
    const map = matched.reduce((acc, p) => {
      (acc[p.trackKey] = acc[p.trackKey] || []).push(p);
      return acc;
    }, {});
    return Object.keys(map).sort().map((track) => ({ track, items: map[track] }));
  }, [personas, filter]);

  if (personas.length === 0) return null;
  if (!isOperator) return null;

  // JWT exp check — see earlier comment about not burning the auth
  // rate limiter on every persona switch.
  const tokenIsLive = (jwt) => {
    if (!jwt) return false;
    try {
      const payload = jwt.split('.')[1];
      const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
      const decoded = JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
      const exp = Number(decoded?.exp || 0);
      return exp > 0 && exp * 1000 > Date.now() + 30000;
    } catch { return false; }
  };

  // Whose session this is, for the "return to…" label. Read from the token
  // the switcher is about to replace, since this component has no auth
  // context of its own.
  const emailFromJwt = (jwt) => {
    try {
      const payload = jwt.split('.')[1];
      const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
      return JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')))?.email || '';
    } catch { return ''; }
  };

  const pick = async (persona) => {
    if (!persona || busy) return;
    setBusy(true);
    setError(null);
    setOpen(false);
    try {
      let accessToken = persona.token;
      if (!tokenIsLive(accessToken)) {
        try {
          const resp = await api.post('/auth/login', {
            email: persona.email,
            password: persona.password
          });
          accessToken = resp?.data?.data?.accessToken
            || resp?.data?.data?.tokens?.accessToken
            || resp?.data?.accessToken;
          const refreshToken = resp?.data?.data?.refreshToken
            || resp?.data?.data?.tokens?.refreshToken
            || resp?.data?.refreshToken;
          if (refreshToken) storage.set(storage.KEYS.REFRESH_TOKEN, refreshToken);
        } catch (err) {
          if (err?.status === 429 || err?.response?.status === 429) {
            throw new Error('Auth rate limit hit. Run `npm run seed:personas` to refresh tokens, or wait 15 minutes.');
          }
          throw err;
        }
      }
      if (!accessToken) throw new Error('No access token available for ' + persona.email);
      // 🔙 Keep a way back BEFORE overwriting the token, or the operator's own
      // session is simply gone — and the switcher that would return them is
      // hidden for the persona they just became (walked 2026-09-10: switched
      // into a reviewer and had no route back to super_admin at all).
      //
      // Only the FIRST hop is remembered: switching persona-to-persona should
      // still return to the human who started, not walk back through a chain.
      try {
        if (!storage.get(storage.KEYS.TEST_PERSONA_RETURN)) {
          const mine = storage.get(storage.KEYS.ACCESS_TOKEN);
          if (mine) {
            storage.set(storage.KEYS.TEST_PERSONA_RETURN, {
              accessToken: mine,
              refreshToken: storage.get(storage.KEYS.REFRESH_TOKEN) || null,
              email: emailFromJwt(mine)
            });
          }
        }
      } catch { /* storage blocked; the switch still works, just without a way back */ }
      // A deliberate change of identity: drop the stamp so the guard learns the new person on the next hydrate.
      storage.remove(storage.KEYS.USER_ID);
      storage.set(storage.KEYS.ACCESS_TOKEN, accessToken);
      // Land on the root, not the page we were on. Reloading in place kept
      // the previous identity's URL — so switching to a customer while on an
      // admin console reloaded straight into a route that identity cannot
      // open, and the switch appeared to fail with "you do not have
      // permission" when it had in fact worked.
      //
      // assign() rather than reload() also gives the new identity its own
      // history entry, so Back does not return to a page it cannot render.
      window.location.assign('/');
    } catch (err) {
      setError(err?.details?.message || err?.message || 'Switch failed');
      setBusy(false);
    }
  };

  const totalShown = grouped.reduce((acc, g) => acc + g.items.length, 0);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className={`inline-flex items-center gap-2 rounded-full border bg-background px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60 ${
          open
            ? 'border-[var(--moksy-text)] text-[var(--moksy-text)]'
            : 'border-[var(--moksy-border)] text-[#374151] hover:border-[#9CA3AF]'
        }`}
        aria-haspopup="listbox"
        aria-expanded={open ? 'true' : 'false'}
        title="Dev-only: switch the active session to a seeded test persona"
      >
        <span className="inline-flex h-[18px] items-center rounded-md bg-[var(--moksy-brand-tint)] px-1.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--moksy-brand-dark)] ring-1 ring-[var(--moksy-brand-border)]">
          Dev
        </span>
        <span>{busy ? 'Switching…' : 'Test as…'}</span>
        <span aria-hidden="true" className="text-[#9CA3AF]">▾</span>
      </button>

      {open ? (
        <div
          className="absolute right-0 z-40 mt-2 w-[320px] rounded-2xl border border-[var(--moksy-border)] bg-background shadow-[0_8px_24px_rgba(15,23,42,0.10)]"
          role="listbox"
        >
          {/* Search header. The list of personas can be 15+; a tiny
              filter saves the scroll. */}
          <div className="border-b border-[var(--moksy-divider)] p-2">
            <input
              ref={inputRef}
              type="search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by name, level, email…"
              className="w-full rounded-lg border border-[var(--moksy-border)] bg-background px-3 py-1.5 text-xs outline-none placeholder:text-[#9CA3AF] focus:border-[var(--moksy-text)]"
            />
          </div>

          <div className="max-h-[420px] overflow-y-auto py-1">
            {grouped.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-[#9CA3AF]">
                No personas match "{filter}".
              </div>
            ) : grouped.map(({ track, items }) => {
              const tone = TONE[trackTone(track)] || TONE.gray;
              return (
                <div key={track} className="py-1">
                  <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#9CA3AF]">
                    {trackLabel(track)}
                    <span className="ml-1 text-[#D1D5DB]">·</span>
                    <span className="ml-1 text-[#9CA3AF]">{items.length}</span>
                  </div>
                  <ul>
                    {items
                      .slice()
                      .sort((a, b) => Number(a.level) - Number(b.level))
                      .map((p) => (
                        <li key={p.email}>
                          <button
                            type="button"
                            onClick={() => pick(p)}
                            className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-[var(--moksy-canvas)]"
                          >
                            {/* Level chip */}
                            <span
                              className={`inline-flex h-7 w-9 shrink-0 items-center justify-center rounded-md text-[11px] font-semibold ring-1 ${tone.chip}`}
                            >
                              L{p.level}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-[var(--moksy-text)]">
                                {p.levelName || `Level ${p.level}`}
                              </span>
                              <span className="block truncate text-[11px] text-[#6B7280]">
                                {p.email}
                              </span>
                            </span>
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Footer — utility row. Includes total + a hint about
              what clicking does so nobody fires it by accident. */}
          <div className="flex items-center justify-between border-t border-[var(--moksy-divider)] px-3 py-2 text-[10px] text-[#9CA3AF]">
            <span>{totalShown} persona{totalShown === 1 ? '' : 's'}</span>
            <span>Reloads the page on pick</span>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="absolute right-0 top-full mt-1 max-w-[320px] rounded-lg border border-rose-200 bg-[#FEF2F2] px-3 py-2 text-[11px] text-rose-800 shadow-sm">
          {error}
          <Button variant="link" size="bare" className="ml-2 opacity-70"
            onClick={() => setError(null)}>
            Dismiss
          </Button>
        </div>
      ) : null}
    </div>
  );
}
